import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { StatCard } from '@/components/StatCard.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { ConfirmDialog } from '@/components/ConfirmDialog.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { useState, useEffect, useRef } from 'react';
import { formatLKR, formatNumber, periodLabel } from '@/lib/format.js';
import { Select } from '@/components/ui/Select.jsx';
import { RefreshCw, Pencil, Eye, FileDown, Lock, Unlock, CheckCircle, Mail, Banknote, FileSpreadsheet, Users, Wallet, ShieldCheck } from 'lucide-react';

import PayslipPreviewDialog from '@/components/PayslipPreviewDialog.jsx';

const STATUS_VARIANT = { draft: 'warning', finalized: 'success', paid: 'brand', reopened: 'info' };

export default function PayrollRunDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirm, setConfirm] = useState(null);

  const queryKey = ['payrollRun', id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => api.get(`/payroll/runs/${id}`) });

  const recompute = useMutation({
    mutationFn: () => api.post(`/payroll/runs/${id}/recompute`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast({ variant: 'success', title: 'Recomputed' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const finalize = useMutation({
    mutationFn: () => api.post(`/payroll/runs/${id}/finalize`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); qc.invalidateQueries({ queryKey: ['payrollRuns'] }); toast({ variant: 'success', title: 'Finalized' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const reopen = useMutation({
    mutationFn: () => api.post(`/payroll/runs/${id}/reopen`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); qc.invalidateQueries({ queryKey: ['payrollRuns'] }); toast({ variant: 'success', title: 'Reopened' }); },
  });
  const markPaid = useMutation({
    mutationFn: () => api.post(`/payroll/runs/${id}/mark-paid`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); qc.invalidateQueries({ queryKey: ['payrollRuns'] }); toast({ variant: 'success', title: 'Marked Paid' }); },
  });
  const emailAll = useMutation({
    mutationFn: () => api.post(`/payslips/run/${id}/email`),
    onSuccess: (r) => toast({ variant: 'success', title: `Queued ${r.queued} emails` }),
  });

  const [previewItem, setPreviewItem] = useState(null); // { id, emp_no, full_name }
  const [noPayDialog, setNoPayDialog] = useState(null); // { employee_id, emp_no, full_name, nopay_days, notes }
  const [filterDept, setFilterDept] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const saveNoPayOverride = useMutation({
    mutationFn: (d) => api.put('/payroll/nopay-overrides', {
      year: d.period_year, month: d.period_month,
      employee_id: d.employee_id, nopay_days: Number(d.nopay_days), notes: d.notes || null,
    }),
    onSuccess: () => {
      recompute.mutate();
      setNoPayDialog(null);
      toast({ variant: 'success', title: 'Override saved — recomputing…' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const clearNoPayOverride = useMutation({
    mutationFn: async (payload) => {
      const overrides = await api.get(`/payroll/nopay-overrides?year=${payload.period_year}&month=${payload.period_month}`);
      const found = overrides.find((o) => o.employee_id === payload.employee_id);
      if (found) await api.del(`/payroll/nopay-overrides/${found.id}`);
    },
    onSuccess: () => {
      recompute.mutate();
      setNoPayDialog(null);
      toast({ variant: 'success', title: 'Override cleared — recomputing…' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  if (isLoading || !data) return <div className="p-4">Loading…</div>;
  const { run, items } = data;

  const departments = [...new Set(items.map((i) => i.department_name).filter(Boolean))].sort();
  const categories  = [...new Set(items.map((i) => i.category_name).filter(Boolean))].sort();
  const filtered = items.filter((i) =>
    (!filterDept || i.department_name === filterDept) &&
    (!filterCat  || i.category_name  === filterCat),
  );

  const totals = (items || []).reduce((s, it) => ({
    gross: s.gross + Number(it.gross_cents),
    net: s.net + Number(it.net_pay_cents),
    epfE: s.epfE + Number(it.epf_employee_cents),
    epfR: s.epfR + Number(it.epf_employer_cents),
    etf: s.etf + Number(it.etf_employer_cents),
    apit: s.apit + Number(it.apit_tax_cents),
  }), { gross: 0, net: 0, epfE: 0, epfR: 0, etf: 0, apit: 0 });

  const columns = [
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'basic_cents', header: 'Basic', accessor: (r) => r.basic_cents, render: (r) => formatNumber(r.basic_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'total_allowances_cents', header: 'Allowances', accessor: (r) => r.total_allowances_cents, render: (r) => formatNumber(r.total_allowances_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'ot_hours', header: 'OT Hours', accessor: (r) => r.ot_hours,
      render: (r) => { const m = Math.round(Number(r.ot_hours || 0) * 60); return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`; },
      className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'ot_amount_cents', header: 'OT Amount', accessor: (r) => r.ot_amount_cents, render: (r) => formatNumber(r.ot_amount_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'nopay_days', header: 'No Pay Days', accessor: (r) => r.nopay_days,
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <span className={Number(r.nopay_days) > 0 ? 'text-red-600 font-semibold' : ''}>{Number(r.nopay_days || 0)}</span>
          {run.status === 'draft' && (
            <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" title="Override nopay days"
              onClick={() => setNoPayDialog({ employee_id: r.employee_id, emp_no: r.emp_no, full_name: r.full_name, nopay_days: r.nopay_days ?? 0, notes: '' })}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      ),
      className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'nopay_amount_cents', header: 'No Pay Amount', accessor: (r) => r.nopay_amount_cents, render: (r) => formatNumber(r.nopay_amount_cents), className: 'text-right tabular-nums font-mono text-red-600', headClassName: 'text-right' },
    { key: 'late_hours', header: 'Late Hours', accessor: (r) => r.late_hours,
      render: (r) => { const m = Math.round(Number(r.late_hours || 0) * 60); return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`; },
      className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'late_deduction_cents', header: 'Late Deduction', accessor: (r) => r.late_deduction_cents, render: (r) => <span style={{ color: '#e10000' }}>{formatNumber(r.late_deduction_cents)}</span>, className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'short_leave_hours', header: 'SL Hours', accessor: (r) => r.short_leave_hours,
      render: (r) => {
        let snap = {};
        if (r.snapshot_json) {
          try { snap = JSON.parse(r.snapshot_json); } catch (e) { /* ignore malformed JSON */ }
        }
        const totalM = (snap.shortLeavePerDay || []).reduce(
          (s, d) => s + (Number(d.shortfallMinutes) || 0), 0
        );
        if (totalM === 0) return '—';
        return <span className="text-emerald-700">{`${Math.floor(totalM / 60)}:${String(totalM % 60).padStart(2, '0')}`}</span>;
      },
      className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'short_leave_count', header: 'SL Count', accessor: (r) => r.short_leave_count,
      render: (r) => {
        let snap = {};
        if (r.snapshot_json) {
          try { snap = JSON.parse(r.snapshot_json); } catch (e) { /* ignore malformed JSON */ }
        }
        const total = (snap.shortLeavePerDay || []).filter((d) => d.forgiven).length;
        return total > 0 ? <span className="text-emerald-700">{total}</span> : '—';
      },
      className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'gross_cents', header: 'Gross', accessor: (r) => r.gross_cents, render: (r) => formatNumber(r.gross_cents), className: 'text-right tabular-nums font-mono font-semibold', headClassName: 'text-right' },
    { key: 'epf_employee_cents', header: 'EPF 8%', accessor: (r) => r.epf_employee_cents, render: (r) => formatNumber(r.epf_employee_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'apit_tax_cents', header: 'APIT', accessor: (r) => r.apit_tax_cents, render: (r) => formatNumber(r.apit_tax_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'loan_recovery_cents', header: 'Loan', accessor: (r) => r.loan_recovery_cents, render: (r) => formatNumber(r.loan_recovery_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'advance_recovery_cents', header: 'Advance', accessor: (r) => r.advance_recovery_cents, render: (r) => formatNumber(r.advance_recovery_cents), className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'total_deductions_cents', header: 'Deductions', accessor: (r) => r.total_deductions_cents, render: (r) => <span className="text-red-600">{formatNumber(r.total_deductions_cents)}</span>, className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: 'net_pay_cents', header: 'Net Pay', accessor: (r) => r.net_pay_cents, render: (r) => <span className="font-semibold text-emerald-700">{formatNumber(r.net_pay_cents)}</span>, className: 'text-right tabular-nums font-mono', headClassName: 'text-right' },
    { key: '__a', header: '', className: 'w-20 text-right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" title="Preview payslip" onClick={() => setPreviewItem({ id: r.id, emp_no: r.emp_no, full_name: r.full_name })}><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Download payslip" onClick={() => api.download(`/payslips/item/${r.id}`, `payslip-${r.emp_no}.pdf`)}><FileDown className="h-4 w-4" /></Button>
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={`Payroll · ${periodLabel(run.period_year, run.period_month)}`}
        description={<span className="inline-flex items-center gap-2">Status: <Badge variant={STATUS_VARIANT[run.status] || 'default'}>{run.status}</Badge></span>}
        actions={
          <div className="flex flex-wrap gap-2">
            {run.status === 'draft' && <Button variant="outline" onClick={() => recompute.mutate()} disabled={recompute.isPending}><RefreshCw className={`h-4 w-4 ${recompute.isPending ? 'animate-spin' : ''}`} />{recompute.isPending ? 'Recomputing…' : 'Recompute'}</Button>}
            {run.status === 'draft' && <Button variant="success" onClick={() => setConfirm('finalize')}><Lock className="h-4 w-4" />Finalize</Button>}
            {(run.status === 'finalized' || run.status === 'paid') && <Button variant="outline" onClick={() => setConfirm('reopen')}><Unlock className="h-4 w-4" />Reopen</Button>}
            {run.status === 'finalized' && <Button onClick={() => setConfirm('paid')}><CheckCircle className="h-4 w-4" />Mark Paid</Button>}
            <Button variant="outline" onClick={() => api.download(`/payslips/run/${id}/zip`, `payslips-${run.period_year}-${run.period_month}.zip`).catch((e) => toast({ variant: 'error', title: 'Download failed', description: e.message }))}><FileDown className="h-4 w-4" />Payslips ZIP</Button>
            <Button variant="outline" onClick={() => emailAll.mutate()}><Mail className="h-4 w-4" />Email All</Button>
            <Button variant="outline" onClick={() => api.download(`/bankfile/run/${id}.csv`, `bankfile-${run.period_year}-${run.period_month}.csv`).catch((e) => toast({ variant: 'error', title: 'Download failed', description: e.message }))}><Banknote className="h-4 w-4" />Bank File</Button>
            <Button variant="outline" onClick={() => api.download(`/reports/payroll/register/${id}.xlsx`, `register-${run.period_year}-${run.period_month}.xlsx`).catch((e) => toast({ variant: 'error', title: 'Download failed', description: e.message }))}><FileSpreadsheet className="h-4 w-4" />Register</Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Employees" value={items.length} icon={Users} accent="brand" />
        <StatCard label="Gross" value={formatLKR(totals.gross)} icon={Wallet} accent="info" />
        <StatCard label="Net Pay" value={formatLKR(totals.net)} icon={Banknote} accent="success" />
        <StatCard label="EPF (E+R)" value={formatLKR(totals.epfE + totals.epfR)} hint={`Employer ${formatLKR(totals.epfR)}`} icon={ShieldCheck} accent="warning" />
      </div>

      <Section title="Payroll Items">
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="w-48">
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
          <Select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="w-48">
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        <div className="relative">
          {recompute.isPending && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm font-medium">Recomputing payroll…</span>
              </div>
            </div>
          )}
          <DataTable columns={columns} data={filtered} pageSize={20} exportFilename={`payroll-${run.period_year}-${run.period_month}.csv`} rowClassName={(r) => (r.is_on_hold ? 'bg-amber-50' : '')} />
        </div>
      </Section>

      <ConfirmDialog
        open={confirm === 'finalize'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Finalize this payroll run?"
        description="Loan installments will be marked paid and advance balances reduced. The run can later be reopened by an Admin."
        confirmText="Finalize"
        variant="success"
        onConfirm={() => finalize.mutate()}
      />
      <ConfirmDialog
        open={confirm === 'reopen'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Reopen this payroll run?"
        description="Loan and advance recoveries will be reversed. You can recompute or edit items."
        confirmText="Reopen"
        variant="outline"
        onConfirm={() => reopen.mutate()}
      />
      <ConfirmDialog
        open={confirm === 'paid'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Mark as Paid?"
        description="Records the payout date. This is informational only."
        confirmText="Mark Paid"
        variant="default"
        onConfirm={() => markPaid.mutate()}
      />

      {previewItem && (
        <PayslipPreviewDialog
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
      )}

      {noPayDialog && (
        <Dialog open onOpenChange={(v) => !v && setNoPayDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Override NoPay Days — {noPayDialog.emp_no} {noPayDialog.full_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">
                Enter no-pay days manually. This overrides attendance-based calculation and persists across recomputes. Remove the override to revert to automatic calculation.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="NoPay Days">
                  <Input type="number" min="0" step="0.5" value={noPayDialog.nopay_days}
                    onChange={(e) => setNoPayDialog({ ...noPayDialog, nopay_days: e.target.value })} />
                </FormField>
                <FormField label="Notes">
                  <Input value={noPayDialog.notes}
                    onChange={(e) => setNoPayDialog({ ...noPayDialog, notes: e.target.value })} />
                </FormField>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="mr-auto text-red-600" disabled={clearNoPayOverride.isPending}
                onClick={() => clearNoPayOverride.mutate({ period_year: run.period_year, period_month: run.period_month, employee_id: noPayDialog.employee_id })}>
                Clear Override
              </Button>
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={() => saveNoPayOverride.mutate({ ...noPayDialog, period_year: run.period_year, period_month: run.period_month })} disabled={saveNoPayOverride.isPending}>
                Save &amp; Recompute
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
